<template>
  <div class="modifiers">
    <b-form-fieldset>
      <legend>Character</legend>
      <div class="row">
        <setting-col>
          <b-form-group label="Initial dice pool" description="Usually AGI + weapon skill.">
            <b-form-input v-model="userData.dicePool" type="number" :formatter="zeroOrGreater" lazy-formatter />
          </b-form-group>
        </setting-col>

        <setting-col>
          <b-form-group label="Condition monitor penalty">
            <b-input-group left="-">
              <b-form-input v-model="userData.conditionPenalty" type="number" :formatter="zeroOrGreater" lazy-formatter />
            </b-input-group>
          </b-form-group>
        </setting-col>

        <setting-col>
          <b-form-group label="Strength">
            <b-form-input v-model="userData.strength" type="number" :formatter="zeroOrGreater" lazy-formatter />
          </b-form-group>
        </setting-col>

        <setting-col>
          <b-form-group label="Willpower">
            <b-form-input v-model="userData.willpower" type="number" :formatter="zeroOrGreater" @change="limitTakeAim" lazy-formatter />
          </b-form-group>
        </setting-col>
      </div>
    </b-form-fieldset>

    <b-form-fieldset>
      <legend>Gear</legend>

      <div class="row">
        <setting-col>
          <b-form-group label="Weapon type">
            <b-form-select v-model="userData.weaponType" :options="staticData.weaponTypeOptions" />
          </b-form-group>
        </setting-col>

        <setting-col v-if="weaponHasRecoil">
          <b-form-group label="Total recoil compensation" description="The total for all weapons you're firing this Action Phase.">
            <b-form-input v-model="userData.recoilCompensation" type="number" :formatter="zeroOrGreater" lazy-formatter />
          </b-form-group>
        </setting-col>

        <setting-col>
          <b-form-group v-if="showWeaponRating" label="Weapon rating">
            <b-form-input v-model="userData.weaponRating" type="number" :formatter="weaponRatingFormatter" lazy-formatter />
          </b-form-group>
        </setting-col>
      </div>

      <div v-if="showIgnoreMinRange">
        <b-form-checkbox v-model="userData.ignoreMinRange">
          Ignore minimum range
        </b-form-checkbox>
      </div>

      <b-form-group label="Environmental compensation">
        <b-form-checkbox v-for="pair in sortedCompensationTypes" v-bind:key="pair.key" v-model="userData.gear[pair.key]">
          {{pair.value}}
        </b-form-checkbox>
      </b-form-group>
    </b-form-fieldset>

    <b-form-fieldset>
      <legend>Environment</legend>
      <div class="row">
        <setting-col>
          <b-form-group label="Visibility">
            <b-form-select v-model="userData.visibility" :options="staticData.visibilityOptions" />
          </b-form-group>
        </setting-col>

        <setting-col>
          <b-form-group label="Light">
            <b-form-select v-model="userData.light" :options="staticData.lightOptions" />
          </b-form-group>
        </setting-col>

        <setting-col>
          <b-form-group label="Glare">
            <b-form-select v-model="userData.glare" :options="staticData.glareOptions" />
          </b-form-group>
        </setting-col>

        <setting-col>
          <b-form-group label="Wind">
            <b-form-select v-model="userData.wind" :options="staticData.windOptions" />
          </b-form-group>
        </setting-col>
      </div>
    </b-form-fieldset>

    <b-form-fieldset>
      <legend>Situational</legend>
      <b-form-checkbox v-for="pair in sortedSitMods" v-bind:key="pair.key" v-model="userData.rangedSitMods[pair.key]">
        {{pair.value}}
      </b-form-checkbox>

      <b-form-radio-group :options="staticData.rangedLOSOptions" v-model="userData.rangedLOS" />

      <div class="row">
        <setting-col>
          <b-form-group label="Distance to target" :state="distanceValid" :description="distanceDescription">
            <b-input-group right="m">
              <b-form-input v-model="userData.distance" type="number" :formatter="zeroOrGreater" :state="distanceValid" lazy-formatter />
            </b-input-group>
            <div v-if="!distanceValid" class="error-feedback">
              Enter a valid distance.
            </div>
          </b-form-group>
        </setting-col>

        <setting-col v-if="weaponHasRecoil">
          <b-form-group label="Bullets fired"
            description="Include current action. Cumulative across Action Phases until an non-shooting action is taken. Ignore when firing a single weapon in Single Shot mode.">
            <b-form-input v-model="userData.bulletsFired" type="number" :formatter="zeroOrGreater" lazy-formatter />
          </b-form-group>
        </setting-col>

        <setting-col v-if="hasWirelessProjectileBonus">
          <b-form-group label="Wireless projectile bonus"
            description="The number of wireless projectiles you've already thrown at your current target this Combat Turn (if your current projectile is also wireless).">
            <b-form-input v-model="userData.wirelessProjectileBonus" type="number" :formatter="zeroOrGreater" lazy-formatter />
          </b-form-group>
        </setting-col>

        <setting-col>
          <b-form-group label="Take Aim bonus" description="Maximum WIL ÷ 2, rounded up.">
            <b-form-input v-model="userData.takeAimBonus" type="number" :formatter="takeAimBonusFormatter" lazy-formatter />
          </b-form-group>
        </setting-col>

        <setting-col>
          <b-form-group label="Other modifiers" description="Use this for any modifiers not covered by other fields.">
            <b-form-input v-model="userData.otherModifiers" type="number" />
          </b-form-group>
        </setting-col>
      </div>
    </b-form-fieldset>

    <div class="row">
      <div class="col-lg-6 offset-lg-3">
        <b-card v-if="allInputValid" bg-variant="trans" border-variant="primary" class="text-center">
          <h3 style="display: inline;">Total dice pool: {{ totalDicePoolString }}</h3>
        </b-card>
        <b-card v-else bg-variant="trans" border-variant="danger" class="text-center">
          <h3 style="display: inline;">No attack possible!</h3>
        </b-card>
      </div>
    </div>
    <div class="row mt-2">
      <div class="col-lg-6 offset-lg-3">
        <div class="btn-group btn-group-justified" role="group">
          <b-button-group>
            <b-button variant="outline-warning" size="lg" @click="clearSituational">
              Clear situational
            </b-button>
          </b-button-group>
          <b-button-group>
            <b-button variant="outline-danger" size="lg" @click="clearUserData">
              Clear all
            </b-button>
          </b-button-group>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Calculate the final environmental modifier from an array of modifier rows
function calcEnvMod (rows) {
  let maxRow = Math.max(...rows)
  let maxCount = 0

  for (let i = 0; i < rows.length; ++i) {
    let row = rows[i]

    if (row === 0) continue

    if (row === maxRow) {
      ++maxCount

      // Two environmental modifiers at the same row, so the max row moves up
      if (maxCount > 1) {
        ++maxRow
        break
      }
    }
  }

  let modifiers = [ 0, -1, -3, -6, -10 ]

  return modifiers[maxRow]
}

// Calculate the target range row
function calcRangeRow (weaponType, strength, distance) {
  let weaponRange = weaponData.getRange(weaponType, strength)

  if (distance < weaponRange[0]) return -1

  let i
  for (i = 1; i < weaponRange.length; ++i) {
    if (weaponRange[i] >= distance) break
  }

  return i - 1
}

// Convert an object to a list of pairs sorted by property values
function objectToValueSortedPairs (obj) {
  let keys = Object.keys(obj)

  // Get pairs of property (key, value)
  let pairs = []
  keys.forEach(function (key) {
    pairs.push({ key: key, value: obj[key] })
  })

  // Sort the pairs by value
  pairs.sort(function (a, b) {
    return a.value.localeCompare(b.value)
  })

  return pairs
}

import staticData from '../../data/modifiers.js'
import weaponData from '../../data/weaponData.js'

// Default values for all data
let defaultUserData = {
  dicePool: 0,
  conditionPenalty: 0,
  strength: 0,
  willpower: 0,

  weaponType: 'taser',
  recoilCompensation: 0,
  weaponRating: 1,
  ignoreMinRange: false,
  gear: {},

  visibility: 0,
  light: 0,
  glare: 0,
  wind: 0,

  distance: 0,
  bulletsFired: 0,
  wirelessProjectileBonus: 0,
  takeAimBonus: 0,
  otherModifiers: 0,
  rangedLOS: 0,
  rangedSitMods: {}
}

let prop
for (prop in Object.assign({}, staticData.compensationGear, staticData.attackGear)) {
  defaultUserData.gear[prop] = false
}

for (prop in staticData.rangedSitMods) {
  defaultUserData.rangedSitMods[prop] = false
}

import settingCol from './setting-col.vue'
import storage from '../../mixins/storage.js'
import formatters from '../../mixins/formatters.js'
import deepCopy from '../../helper/deepCopy.js'

export default {
  name: 'modifiers',
  components: {settingCol},
  mixins: [storage, formatters],

  data () {
    return {
      dataId: 'modifiers',
      defaultUserData: defaultUserData,
      staticData: staticData,
      error: ''
    }
  },

  methods: {
    // FORMATTERS //
    takeAimBonusFormatter (value) {
      return this.zeroOrGreater(Math.min(value, this.maxTakeAimBonus))
    },

    weaponRatingFormatter (value) {
      return Math.min(Math.max(value, 1), 10)
    },

    // EVENTS //
    limitTakeAim () {
      this.userData.takeAimBonus = Math.min(this.userData.takeAimBonus, this.maxTakeAimBonus)
    },

    // Clear situational data
    clearSituational () {
      const situationalParams = [
        'distance', 'bulletsFired', 'wirelessProjectileBonus', 'takeAimBonus', 'otherModifiers', 'rangedSitMods', 'rangedLOS'
      ]

      for (let i = 0; i < situationalParams.length; ++i) {
        let param = situationalParams[i]
        this.userData[param] = deepCopy(defaultUserData[param])
      }
    }
  },

  computed: {
    // DISPLAY/VALIDATION //
    // True if all inputs are valid
    allInputValid () {
      return this.distanceValid
    },

    // True if the target distance is valid
    distanceValid () {
      return this.weaponRange >= 0 && this.weaponRange < 4
    },

    // Whether to show the "minimum range disarmed" checkbox
    showIgnoreMinRange () {
      return weaponData.getRange(this.userData.weaponType, 1)[0] > 0
    },

    // Whether to show the weapon rating
    showWeaponRating () {
      return this.userData.weaponType === 'bow'
    },

    // Sorted compensation types
    sortedCompensationTypes () {
      let combinedAttackGear = Object.assign({}, this.staticData.compensationGear, this.staticData.attackGear)
      return objectToValueSortedPairs(combinedAttackGear)
    },

    // Sorted situational modifier types
    sortedSitMods () {
      return objectToValueSortedPairs(this.staticData.rangedSitMods)
    },

    // Description under the distance to target field
    distanceDescription () {
      let description = ''

      let ranges = weaponData.getRange(this.userData.weaponType, this.userData.strength)
      let minRange = ranges[0]
      let maxRange = ranges[ranges.length - 1]

      if (minRange > 0) {
        description += 'Min range: ' + minRange + ' m; max range: ' + maxRange + ' m'
      }
      else {
        description = 'Max range: ' + maxRange + ' m'
      }

      return description
    },

    // Whether the weapon is affected by recoil
    weaponHasRecoil () {
      return weaponData.hasRecoil(this.userData.weaponType)
    },

    // Whether the weapon has a bonus for each one thrown at the same target
    hasWirelessProjectileBonus () {
      return this.userData.weaponType === 'shuriken' || this.userData.weaponType === 'knife'
    },

    // STAT CALCULATION //
    // The weapon's range row
    weaponRange () {
      let rangeRow = calcRangeRow(this.userData.weaponType, parseInt(this.userData.strength), parseInt(this.userData.distance))

      // This should only come up on weapons with a minimum range (e.g. grenade/missile launchers)
      if (this.userData.ignoreMinRange && rangeRow < 0) {
        rangeRow = 0
      }

      return rangeRow
    },

    // The compensated visibility row
    compensatedVisibility () {
      let visibility = parseInt(this.userData.visibility)

      if (this.userData.gear.thermographicVision) visibility -= 1
      if (this.userData.gear.ultrasound) visibility -= 1

      return Math.max(visibility, 0)
    },

    // The compensated light row
    compensatedLight () {
      // Ultrasound ignores all light conditions within 50 meters
      if (this.userData.gear.ultrasound && this.userData.distance <= 50) {
        return 0
      }

      let light = parseInt(this.userData.light)

      if (this.userData.gear.sunglasses) light += 1
      // Low-light vision negates all light modifiers below full light.
      // I assume you apply this after sunglasses (you can see the light coming through them directly),
      // but before thermographic vision (it doesn't improve the "light" level of heat coming off of things)
      if (this.userData.gear.lowLightVision && light <= 2) return 0
      if (this.userData.gear.thermographicVision) light -= 1

      return Math.max(light, 0)
    },

    // The compensated glare row
    compensatedGlare () {
      let glare = parseInt(this.userData.glare)

      if (this.userData.gear.implantedFlareCompensation || this.userData.gear.externalFlareCompensation) glare -= 2
      if (this.userData.gear.sunglasses) glare -= 1

      return Math.max(glare, 0)
    },

    // The compensated wind row
    compensatedWind () {
      let wind = parseInt(this.userData.wind)

      // Only wind stronger than light winds is improved by tracer rounds
      if (this.userData.gear.tracerRounds && wind > 1) wind -= 1
      if (this.userData.gear.implantedSmartlink || this.userData.gear.externalSmartLink) wind -= 1

      return Math.max(wind, 0)
    },

    // The compensated weapon range
    compensatedWeaponRange () {
      let range = parseInt(this.weaponRange)

      if (this.userData.gear.imageMagnification) range -= 1
      if (this.userData.gear.tracerRounds) range -= 1

      return Math.max(range, 0)
    },

    // Maximum Take Aim bonus
    maxTakeAimBonus () {
      return Math.ceil(this.userData.willpower / 2)
    },

    // Total recoil compensation, including free point and strength bonus
    totalRecoilCompensation () {
      return 1 + Math.ceil(parseInt(this.userData.strength) / 3) + parseInt(this.userData.recoilCompensation)
    },

    // Calculate the total dice pool modifier
    totalModifier () {
      let envRows = [
        this.compensatedVisibility,
        this.compensatedLight,
        this.compensatedGlare,
        this.compensatedWind,
        this.compensatedWeaponRange
      ]

      let finalMod = calcEnvMod(envRows)

      // Apply bonus if both a smartlink and smartgun are equipped
      let smartgunBonus = 0
      if (this.userData.gear.smartgun) {
        if (this.userData.gear.implantedSmartlink) smartgunBonus = 2
        else if (this.userData.gear.externalSmartlink) smartgunBonus = 1
      }
      finalMod += smartgunBonus

      // Laser sight bonus doesn't stack with smartgun bonuses
      // Note that with how this is set up, it *does* stack with smartgun wind/range compensation. It's not clear
      // by the rules whether it should or not. For example, if the wind row is 1 and the character has both tracer
      // rounds and a smartlink, do they still get the laser sight bonus? I leave this up to the user to turn on/off
      // depending on their interpretation.
      if (smartgunBonus === 0 && this.userData.gear.laserSight) finalMod += 1

      // Situational modifiers
      if (this.userData.rangedSitMods.calledShot) finalMod -= 4
      if (this.userData.rangedSitMods.firingFromVehicle) finalMod -= 2
      if (this.userData.rangedSitMods.inMeleeCombat) finalMod -= 3
      if (this.userData.rangedSitMods.running) finalMod -= 2
      if (this.userData.rangedSitMods.offhand) finalMod -= 2

      finalMod -= parseInt(this.userData.conditionPenalty)
      finalMod += parseInt(this.userData.takeAimBonus)
      finalMod += parseInt(this.userData.otherModifiers)

      // Direct LOS = 0; firing from cover = -3; firing blind = -6
      finalMod -= parseInt(this.userData.rangedLOS) * 3

      // Penalty for being too weak to fire a bow
      if (this.userData.weaponType === 'bow') {
        let strengthDiff = this.userData.strength - this.userData.weaponRating
        if (strengthDiff < 0) finalMod += strengthDiff * 3
      }

      // Recoil penalty
      if (this.weaponHasRecoil) {
        let recoilTotal = this.totalRecoilCompensation - parseInt(this.userData.bulletsFired)
        if (recoilTotal < 0) finalMod += recoilTotal
      }

      // Wireless thrown weapon bonus
      if (this.hasWirelessProjectileBonus) {
        finalMod += parseInt(this.userData.wirelessProjectileBonus)
      }

      return finalMod
    },

    // The formatted dice pool string
    totalDicePoolString: function () {
      let totalDicePool = parseInt(this.userData.dicePool) + this.totalModifier

      if (totalDicePool < 0) {
        return '0 (' + totalDicePool + ')'
      }

      return totalDicePool
    }
  }
}
</script>



// WEBPACK FOOTER //
// Modifiers.vue?0860a136